import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { loginInput, type LoginInput } from '@portal-leo/shared';
import { Button, Input, Card, CardContent } from '@/components/ui';
import { useAuth, useAuthStore } from '@/hooks/useAuth';
import { trpc, setAuthToken } from '@/lib/trpc';

const registerSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  senha: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  confirmSenha: z.string().min(6, 'Confirme a senha'),
}).refine((data) => data.senha === data.confirmSenha, {
  message: 'As senhas não coincidem',
  path: ['confirmSenha'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export function LoginPage() {
  const { isAuthenticated, login, isLoading } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const [mode, setMode] = useState<'login' | 'register'>('login');

  const loginForm = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
  });

  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const utils = trpc.useUtils();
  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: (data) => {
      setAuthToken(data.token);
      setUser(data.user);
      utils.invalidate();
    },
  });

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onLogin = async (data: LoginInput) => {
    try {
      await login(data);
    } catch {
      loginForm.setError('root', { message: 'Email ou senha inválidos' });
    }
  };

  const onRegister = async (data: RegisterFormData) => {
    try {
      await registerMutation.mutateAsync({
        nome: data.nome,
        email: data.email,
        senha: data.senha,
      });
    } catch (err: any) {
      registerForm.setError('root', {
        message: err.message || 'Erro ao criar conta',
      });
    }
  };

  const switchMode = (newMode: 'login' | 'register') => {
    setMode(newMode);
    loginForm.reset();
    registerForm.reset();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-primary-600">Portal LEO Clube</h1>
            <p className="mt-2 text-gray-600">
              {mode === 'login' ? 'Faça login para continuar' : 'Crie sua conta'}
            </p>
          </div>

          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <Input
                id="email"
                type="email"
                label="Email"
                placeholder="seu@email.com"
                error={loginForm.formState.errors.email?.message}
                {...loginForm.register('email')}
              />

              <Input
                id="senha"
                type="password"
                label="Senha"
                placeholder="••••••"
                error={loginForm.formState.errors.senha?.message}
                {...loginForm.register('senha')}
              />

              <div className="text-right">
                <Link
                  to="/esqueci-senha"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Esqueci minha senha
                </Link>
              </div>

              {loginForm.formState.errors.root && (
                <p className="text-sm text-red-600">{loginForm.formState.errors.root.message}</p>
              )}

              <Button type="submit" className="w-full" isLoading={isLoading}>
                Entrar
              </Button>

              <p className="text-center text-sm text-gray-600">
                Não tem conta?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('register')}
                  className="font-medium text-primary-600 hover:text-primary-700"
                >
                  Cadastre-se
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
              <Input
                id="nome"
                label="Nome completo"
                placeholder="Seu nome"
                error={registerForm.formState.errors.nome?.message}
                {...registerForm.register('nome')}
              />

              <Input
                id="reg-email"
                type="email"
                label="Email"
                placeholder="seu@email.com"
                error={registerForm.formState.errors.email?.message}
                {...registerForm.register('email')}
              />

              <Input
                id="reg-senha"
                type="password"
                label="Senha"
                placeholder="Mínimo 6 caracteres"
                error={registerForm.formState.errors.senha?.message}
                {...registerForm.register('senha')}
              />

              <Input
                id="confirm-senha"
                type="password"
                label="Confirmar senha"
                placeholder="Repita a senha"
                error={registerForm.formState.errors.confirmSenha?.message}
                {...registerForm.register('confirmSenha')}
              />

              {registerForm.formState.errors.root && (
                <p className="text-sm text-red-600">{registerForm.formState.errors.root.message}</p>
              )}

              <Button type="submit" className="w-full" isLoading={registerMutation.isPending}>
                Criar conta
              </Button>

              <p className="text-center text-sm text-gray-600">
                Já tem conta?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="font-medium text-primary-600 hover:text-primary-700"
                >
                  Entrar
                </button>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
